import {
	Text,
	HStack,
	Button,
	Select,
	IconButton,
	VStack,
	Checkbox,
	Modal,
	ModalBody,
	ModalCloseButton,
	ModalContent,
	ModalFooter,
	ModalHeader,
	ModalOverlay,
	useDisclosure,
	Popover,
	PopoverArrow,
	PopoverBody,
	PopoverContent,
	PopoverTrigger,
	Portal,
} from "@chakra-ui/react";
import { formatShiftDate, formatShiftHours, updateDateOnly } from "@utils/utils";
import BlackClose from "@components/icons/BlackClose";
import { BreakTimeList, ShiftTimeList } from "@constants/createOrder";
import { getFormatTime, getTimeDiffInHours } from "@utils/dateUtils";
import type { ShiftItem } from "@models/Order";
import moment from "moment";
import { DatePicker } from "./DatePicker";
import type { ReactNode } from "react";
import { useEffect } from "react";

interface Props {
	shift: ShiftItem;
	onChange: (shift: ShiftItem) => void;
	onRemove: () => any;
	avatar: ReactNode;
	editable?: boolean;
	bookedConsultantName?: string;
	isLoadingDeleteShift?: boolean;
	consultantAbsent?: boolean;
}

export const ShiftRow: React.FC<Props> = ({
	shift,
	onRemove,
	onChange,
	bookedConsultantName = "",
	editable = false,
	isLoadingDeleteShift,
	avatar,
	consultantAbsent = false,
}) => {
	const startTimeAndEndTimeDiff =
		getFormatTime(shift.start) !== "00:00" && getFormatTime(shift.end) !== "00:00"
			? getTimeDiffInHours(getFormatTime(shift.start), getFormatTime(shift.end))
			: -1;
	const { isOpen, onOpen, onClose } = useDisclosure();

	useEffect(() => {
		if (!isLoadingDeleteShift) onClose();
	}, [isLoadingDeleteShift]);

	const handleChangeDay = (newDay?: Date) => {
		onChange({
			...shift,
			start: updateDateOnly(shift.start, newDay),
			end: updateDateOnly(shift.end, newDay),
		});
	};

	return (
		<>
			{!editable ? (
				<HStack justifyContent={"space-between"} w={"100%"}>
					<HStack>
						{avatar}
						<VStack alignItems={"flex-start"} minW={"100px"}>
							<Text fontSize={"17px"} fontWeight={500} m={0}>
								{formatShiftDate(shift.start)}
							</Text>
							{shift.flexible && (
								<Text m={0} fontSize={"13px"} color={"#999999"} fontWeight={400}>
									Flexibel starttid
								</Text>
							)}
						</VStack>
					</HStack>
					<Text fontSize={"17px"} fontWeight={400} m={1}>{`${formatShiftHours(shift.start)} - ${formatShiftHours(
						shift.end,
					)}`}</Text>
					<Text
						fontSize={"17px"}
						fontWeight={400}
						width={"220px"}
						m={1}
						textAlign={"end"}
						as={bookedConsultantName ? "p" : "i"}
						color={bookedConsultantName ? "#333333" : "#999999"}
					>
						{bookedConsultantName ? bookedConsultantName : "Konsult ej tilsatt"}
						{bookedConsultantName && consultantAbsent && (
							<Text as="span" color={"#219CD0"} fontWeight={500} ml={1}>
								(frånvarande)
							</Text>
						)}
					</Text>
				</HStack>
			) : (
				<HStack justifyContent={"space-between"} w={"100%"}>
					<HStack>
						{avatar}
						<VStack alignItems={"flex-start"} gap={0} w={"150px"}>
							<Popover>
								<PopoverTrigger>
									<Button
										width={"fit-content"}
										fontSize={"17px"}
										fontWeight={500}
										size="sm"
										_hover={{ opacity: 0.8 }}
										color={"accent.500"}
										variant="link"
									>
										{formatShiftDate(shift.start)}
									</Button>
								</PopoverTrigger>
								<Portal>
									<PopoverContent>
										<PopoverArrow />
										<PopoverBody>
											<DatePicker value={new Date(shift.start)} onChange={handleChangeDay} compact />
										</PopoverBody>
									</PopoverContent>
								</Portal>
							</Popover>

							{moment().isSame(shift.start, "day") && (
								<Checkbox
									size="sm"
									defaultChecked={shift.flexible ? true : false}
									onChange={(e) => onChange({ ...shift, flexible: e.target.checked })}
									m={0}
									fontSize={"13px"}
									color={"#999999"}
									fontWeight={400}
								>
									Flexibel starttid
								</Checkbox>
							)}
						</VStack>
					</HStack>
					<VStack>
						<HStack width={"220px"} justifyContent={"space-between"}>
							<Select
								fontSize={"16px"}
								fontWeight={400}
								onChange={(e) =>
									onChange({
										...shift,
										// start: `${getFormatDate(shift.start)}T${e.target.value ? e.target.value : "00:00"}:00`,
										start: e.target.value
											? moment(shift.start)
													.set({
														hour: Number(e.target.value.split(":")[0]),
														minute: Number(e.target.value.split(":")[1]),
													})
													.format()
											: shift.start,
									})
								}
								value={getFormatTime(shift.start)}
								size="sm"
								width={"95px"}
								borderRadius={"lg"}
							>
								{ShiftTimeList.map(({ key, value }: { key: string; value: string }) => (
									<option key={key} value={key}>
										{value}
									</option>
								))}
							</Select>
							<Text>till</Text>
							<Select
								fontSize={"16px"}
								fontWeight={400}
								onChange={(e) =>
									onChange({
										...shift,
										// end: `${getFormatDate(shift.end)}T${e.target.value ? e.target.value : "00:00"}:00`,
										end: e.target.value
											? moment(shift.end)
													.set({
														hour: Number(e.target.value.split(":")[0]),
														minute: Number(e.target.value.split(":")[1]),
													})
													.format()
											: shift.end,
									})
								}
								value={getFormatTime(shift.end)}
								size="sm"
								width={"95px"}
								borderRadius={"lg"}
							>
								{ShiftTimeList.map(({ key, value }: { key: string; value: string }) => (
									<option key={key} value={key}>
										{value}
									</option>
								))}
							</Select>
						</HStack>
						{startTimeAndEndTimeDiff >= 0 && startTimeAndEndTimeDiff < 4 && (
							<Text fontWeight={500} color={"#EB5757"} fontSize={"12px"} m="0" mt="-1">
								Passet måste vara minst 4 timmar
							</Text>
						)}
					</VStack>
					<Select
						fontSize={"16px"}
						fontWeight={400}
						size="sm"
						width={"150px"}
						borderRadius={"lg"}
						onChange={(e) => onChange({ ...shift, break: Number(e.target.value) })}
						value={shift?.break ? shift?.break.toString() : "0"}
					>
						{BreakTimeList.map(({ key, value }: { key: string; value: string }) => (
							<option key={key} value={key}>
								{`${value} min rast`}
							</option>
						))}
					</Select>
					<IconButton
						variant="ghost"
						size={"xs"}
						aria-label="remove shift"
						isRound={true}
						onClick={onOpen}
						icon={<BlackClose />}
					/>
				</HStack>
			)}
			<Modal isOpen={isOpen} onClose={onClose} size={"lg"}>
				<ModalOverlay />
				<ModalContent>
					<ModalHeader textAlign={"center"}>Ta bort pass</ModalHeader>
					<ModalCloseButton />
					<ModalBody>
						<Text textAlign={"center"}>{`Är du säker på att du vill ta bort arbetsspasset ${formatShiftDate(
							shift.start,
						)} ?`}</Text>
					</ModalBody>

					<ModalFooter>
						<VStack width={"100%"}>
							<Button
								borderRadius={"2px"}
								_hover={{ background: "#4e7da7bd" }}
								bg="accent.500"
								color={"#FFFFFF"}
								width={"100%"}
								mx={3}
								onClick={() => {
									onRemove && onRemove();
								}}
								isLoading={isLoadingDeleteShift}
							>
								Ja, ta bort
							</Button>
							<Button
								isLoading={isLoadingDeleteShift}
								borderRadius={"2px"}
								onClick={onClose}
								width={"100%"}
								mx={3}
								variant="ghost"
							>
								Nej
							</Button>
						</VStack>
					</ModalFooter>
				</ModalContent>
			</Modal>
		</>
	);
};
